import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useUpsertVacancyMutation,
  useSetVacancyStatusMutation,
  useBulkPublishVacanciesMutation,
  useDeleteVacancyMutation,
  useBulkSetVacancyStatusMutation,
  VacancyInput,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { careerErrorMessage } from './careerModel';

interface IVacancyMutationsHook {
  /** Create when `id` is omitted, update when it is given. */
  saveVacancy: (input: VacancyInput, id?: string) => Promise<boolean>;
  /** Move one vacancy along the DRAFT→…→ARCHIVED workflow. */
  setStatus: (id: string, status: string) => Promise<boolean>;
  /** Publish many drafts at once; resolves with the count actually flipped. */
  bulkPublish: (ids: string[]) => Promise<number | null>;
  /**
   * Spec 0035 — move many rows to any status in ONE set-based server call.
   * Rejecting a night's agent drafts used to be one mutation per row, each
   * dragging a full table reload behind it.
   */
  bulkSetStatus: (ids: string[], status: string) => Promise<number | null>;
  /**
   * Spec 0034 — hard-delete a draft/rejected vacancy. The backend refuses
   * anything further along the workflow with `vacancy_locked`, which arrives
   * as an error toast pointing at the archive.
   */
  deleteVacancy: (id: string) => Promise<boolean>;
  pending: boolean;
}

/**
 * Spec 0018 increment B — vacancy-side mutations for the Вакансии tab.
 *
 * Every failure surfaces the mapped `customErrors` code as an error toast; the
 * caller owns success toasts where the phrasing depends on counts (bulk
 * publish). The envelope is checked before the catch so a backend validation
 * error (e.g. dedup collision) reads as a toast, not a thrown exception.
 */
export const useVacancyMutations = (
  onChanged?: () => void,
): IVacancyMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [saveRun] = useUpsertVacancyMutation();
  const [statusRun] = useSetVacancyStatusMutation();
  const [bulkRun] = useBulkPublishVacanciesMutation();
  const [deleteRun] = useDeleteVacancyMutation();
  const [bulkStatusRun] = useBulkSetVacancyStatusMutation();
  const [pending, setPending] = useState(false);

  const toastError = useCallback(
    (title: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.CareerCenter,
        data: { type: ToastTypes.error, title, text },
      });
    },
    [addToast],
  );

  const saveVacancy: IVacancyMutationsHook['saveVacancy'] = useCallback(
    async (input, id) => {
      setPending(true);
      try {
        const result = await saveRun({ variables: { id, input } });
        const payload = result.data?.upsertVacancy;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastSaveError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastSaveError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [saveRun, onChanged, toastError, t],
  );

  const setStatus: IVacancyMutationsHook['setStatus'] = useCallback(
    async (id, status) => {
      setPending(true);
      try {
        const result = await statusRun({
          variables: { id, status },
          // 🔴 Никакого `onChanged()` → `refetch()` здесь, сознательно.
          //
          // Раньше каждое действие по строке перезагружало ВСЮ таблицу:
          // разбор трёхсот ночных черновиков стоил 300 мутаций и 300 полных
          // выгрузок. Мутация и так возвращает вакансию, поэтому сущность в
          // кэше Apollo обновляет по id сам; руками остаётся убрать строку из
          // отфильтрованного списка, когда её новый статус ему больше не
          // подходит, — иначе разобранная очередь показывает уже обработанное.
          //
          // Замерено в браузере: одно действие = мутация + ОДНА страница
          // (`cache.modify` инвалидирует поле, и `cache-and-network` дочитывает
          // 20 строк), а не вся доска. Дочитывание тут кстати: страница
          // остаётся полной, и очередь не тает до пустого экрана.
          update(cache, { data }) {
            const vacancy = data?.setVacancyStatus?.vacancy;
            if (!vacancy) return;
            cache.modify({
              fields: {
                // Поле именно `careerVacancyBoard` — доска переехала на него в
                // 0035. Правка кэша по имени поля ПРОМАХИВАЕТСЯ МОЛЧА: старое
                // имя не вызвало бы ни ошибки, ни предупреждения, просто
                // обработанная строка навсегда осталась бы висеть в очереди.
                careerVacancyBoard(existing, { readField, storeFieldName }) {
                  if (!existing?.edges) return existing;
                  // `storeFieldName` несёт сериализованные аргументы, поэтому
                  // видно, КАКОЙ отфильтрованный список перед нами, и чистится
                  // только тот, которому строка больше не принадлежит.
                  const filtered = /"status":"([a-z]+)"/.exec(storeFieldName);
                  if (!filtered || filtered[1] === vacancy.status)
                    return existing;
                  const edges = existing.edges.filter(
                    (edge: { node: unknown }) =>
                      readField('id', edge.node as never) !== vacancy.id,
                  );
                  if (edges.length === existing.edges.length) return existing;
                  return {
                    ...existing,
                    edges,
                    // Счётчик обязан уехать вместе со строкой: «Найдено: 66»
                    // над списком из 65 — это то же враньё, что не убранная
                    // строка, только тише.
                    totalCount: Math.max(0, (existing.totalCount ?? 1) - 1),
                  };
                },
              },
            });
          },
        });
        const payload = result.data?.setVacancyStatus;
        if (payload?.success) {
          return true;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastStatusError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastStatusError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [statusRun, toastError, t],
  );

  const bulkPublish: IVacancyMutationsHook['bulkPublish'] = useCallback(
    async (ids) => {
      if (!ids.length) return 0;
      setPending(true);
      try {
        const result = await bulkRun({ variables: { ids } });
        const payload = result.data?.bulkPublishVacancies;
        if (payload?.success) {
          onChanged?.();
          return payload.published ?? 0;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastPublishError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastPublishError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setPending(false);
      }
    },
    [bulkRun, onChanged, toastError, t],
  );

  const deleteVacancy: IVacancyMutationsHook['deleteVacancy'] = useCallback(
    async (id) => {
      setPending(true);
      try {
        const result = await deleteRun({ variables: { id } });
        const payload = result.data?.deleteVacancy;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastDeleteError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastDeleteError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [deleteRun, onChanged, toastError, t],
  );

  const bulkSetStatus: IVacancyMutationsHook['bulkSetStatus'] = useCallback(
    async (ids, status) => {
      if (!ids.length) return 0;
      setPending(true);
      try {
        const result = await bulkStatusRun({ variables: { ids, status } });
        const payload = result.data?.bulkSetVacancyStatus;
        if (payload?.success) {
          // Bulk DOES refetch, unlike a single row: an unknown number of rows
          // changed and the counters above the table have to agree with them.
          onChanged?.();
          return payload.moved ?? 0;
        }
        toastError(
          t('pages.careerCenter.vacancies.toastStatusError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.vacancies.toastStatusError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setPending(false);
      }
    },
    [bulkStatusRun, onChanged, toastError, t],
  );

  return {
    saveVacancy,
    setStatus,
    bulkPublish,
    bulkSetStatus,
    deleteVacancy,
    pending,
  };
};
